import {
  FieldDefinitionEnumType,
  FilterDefinitionType,
  FilterType,
} from "@/components/@types";
import { DatePicker } from "@/components/organisms/DatePicker";
import { FieldEnum } from "@/components/organisms/Field/FieldEnum";
import { List } from "@/components/organisms/List";
import { NodeList } from "@/components/organisms/Node/NodeList";
import { getReferenceValueOptionsByTypename } from "@/objects";
import { State } from "@/state";
import { FilterChipValuePickerForNumber } from "@/components/organisms/UtilityBar/FilterChip/FilterChipValuePickerForNumber";
import { FilterChipValuePickerForText } from "@/components/organisms/UtilityBar/FilterChip/FilterChipValuePickerForText";
import { useFilterChipMutations } from "@/components/organisms/UtilityBar/FilterChip/FilterChipStateContext";

export function FilterChipValuePicker({
  filter,
  filterDefinition,
  setIsValueOpen,
  filterIndex,
}: {
  filter: FilterType;
  filterDefinition: FilterDefinitionType;
  setIsValueOpen: (isOpen: boolean) => void;
  filterIndex: number;
}) {
  const { setFilters, updateFilterAtIndex } = useFilterChipMutations();

  if (filterDefinition.type === "text") {
    return (
      <FilterChipValuePickerForText
        filter={filter}
        filterIndex={filterIndex}
        filterDefinition={filterDefinition}
        onChange={(partialState) => {
          if (partialState.filters) {
            setFilters(partialState.filters);
          }
        }}
        setIsOpen={setIsValueOpen}
      />
    );
  }

  if (
    filterDefinition.type === "number" ||
    filterDefinition.type === "currency"
  ) {
    return (
      <FilterChipValuePickerForNumber
        filter={filter}
        filterIndex={filterIndex}
        filterDefinition={filterDefinition}
        onChange={(partialState) => {
          if (partialState.filters) {
            setFilters(partialState.filters);
          }
        }}
        setIsOpen={setIsValueOpen}
      />
    );
  }

  if (filterDefinition.type === "date") {
    return (
      <DatePicker
        onChange={() => setIsValueOpen(false)}
        date1={
          (filter.value as any)?.date1
            ? new Date((filter.value as any).date1)
            : new Date()
        }
        setDate1={(date) => {
          updateFilterAtIndex(filterIndex, (current) => ({
            ...current,
            value: {
              ...(current.value as any),
              date1: date?.toISOString(),
            },
          }));
        }}
        {...(filter.operator === "range" && {
          date2: (filter.value as any)?.date2
            ? new Date((filter.value as any).date2)
            : new Date(),
          setDate2: (date) => {
            updateFilterAtIndex(filterIndex, (current) => ({
              ...current,
              value: {
                ...(current.value as any),
                date2: date?.toISOString(),
              },
            }));
          },
        })}
      />
    );
  }

  if (filterDefinition.type === "reference") {
    // A filter looks across every active entity, so it offers every active
    // entity's records — not the one entity a field on a single record would,
    // nor another's to bring into it.
    const valueOptions = filterDefinition.referenceTypename
      ? getReferenceValueOptionsByTypename(filterDefinition.referenceTypename)(
          State.getState().activeEntityIdentifiers,
          "filter",
        )
      : filterDefinition?.getValueOptions(undefined);
    return (
      <NodeList
        {...(valueOptions as any)}
        {...(["inList", "notInList"].includes(filter.operator) && {
          isMultiselect: true,
          onSubmitCheckedRows: (checkedRows) => {
            setIsValueOpen(false);
            updateFilterAtIndex(filterIndex, (current) => ({
              ...current,
              value:
                checkedRows.length === 0
                  ? null
                  : checkedRows.map((row: any) => row.identifier),
            }));
          },
          getIsChecked(option: any) {
            return (filter.value as any)?.includes(option.identifier);
          },
        })}
        {...(!["inList", "notInList"].includes(filter.operator) && {
          onSelectOption: (item: any) => {
            setIsValueOpen(false);
            updateFilterAtIndex(filterIndex, (current) => ({
              ...current,
              value: item.identifier,
            }));
          },
        })}
      />
    );
  }

  if (filterDefinition.type === "enum") {
    return (
      <List<
        ReturnType<FieldDefinitionEnumType["getValueOptions"]>["enum"][number]
      >
        data={(filterDefinition.getValueOptions(undefined) as any)?.enum}
        getRowKey={(option) => option.key}
        {...(["inList", "notInList"].includes(filter.operator) && {
          isMultiselect: true,
          isCheckboxesEnabled: true,
          onSubmitCheckedRows: (checkedRows) => {
            setIsValueOpen(false);
            updateFilterAtIndex(filterIndex, (current) => ({
              ...current,
              // The values the API is asked with, as one picked is: an option's
              // ``key`` is the name it is listed under (``BankStatement``).
              value:
                checkedRows.length === 0
                  ? null
                  : checkedRows.map((row) => row.value ?? row.key),
            }));
          },
          getIsChecked(option) {
            const selected = Array.isArray(filter.value)
              ? filter.value.map(String)
              : [];
            return (
              selected.includes(String(option.key)) ||
              selected.includes(String(option.value))
            );
          },
        })}
        {...(!["inList", "notInList"].includes(filter.operator) && {
          onItemClick: (item) => {
            setIsValueOpen(false);
            updateFilterAtIndex(filterIndex, (current) => ({
              ...current,
              value: item.value,
            }));
          },
        })}
        className="min-w-[calc(320px)] max-w-[calc(640px)]"
      >
        <List.Items>
          {(option: any) => (
            <List.Item
              value={option}
              data-testid={`filter-value-option-${option.value}`}
            >
              <FieldEnum
                value={option.value}
                valueOptions={
                  filterDefinition.getValueOptions(undefined) as any
                }
                isMultiline={false}
              />
            </List.Item>
          )}
        </List.Items>
        {["inList", "notInList"].includes(filter.operator) && (
          <List.MultiSelectFooter />
        )}
      </List>
    );
  }

  return null;
}
